import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Form, Button, Label, Icon } from 'semantic-ui-react';
import { DatePicker } from '../shared/DatePicker';
import WeekDaysSelect from '../shared/WeekDaysSelect';
import PropTypes from 'prop-types';
import { onSuccess, onError, onWarning } from '../shared/messages';
import RatesDropDown from '../shared/RatesDropDown';
import RoomTypesDropDown from '../shared/RoomTypesDropDown';
import PriceGroupsDropDown from '../shared/PriceGroupsDropDown';
import ProductsDropDown from '../shared/ProductsDropDown';
import RestrictionTypesDropDown from '../shared/RestrictionTypesDropDown';
import CustomerDropDown from "../shared/CustomerDropDown";
import { LiveUpdateHierarchyData } from '../../api/services/pms';
import { Products, Rates, Packages, RoomTypes } from "../../api/services/next"
import { Addons } from "../../api/services/next";
import { useMessageResources, useTextResources } from '../../language';
import moment from 'moment';
import NumericInput from '../shared/NumericInput';
import { isMobileOnly, isBrowser } from 'mobile-device-detect';
import { useLoader } from '../../hooks/useLoader';
import ValidateModalPermission from '../shared/ValidateModalPermission';
import { PermissionsActions } from '../shared/permissionsActions';
import { RateCategory, serverDateFormat } from '../shared/constants'
import AllowExternalUpdatesDropDown from '../shared/AllowExternalUpdatesDropDown';
import DropDown from '../shared/dropdown';
import HotelsDropDown from '../shared/HotelsDropDown';
import CustomerGroupDropDown from "../shared/CustomerGroupDropDown";
import { useConfirm } from "../../hooks/useConfirm";
import PackagesDropDown from '../shared/PackagesDropDown';
import { toServerDateFormat } from '../shared/utils';

const RestrictionsPopup = ({
    onClose,
    onSave,
    HotelID,
    controller,
    startOpen,
    initialDate,
    open: openProp,
    ...rest
}) => {
    const text = useTextResources();
    const message = useMessageResources();
    const loader = useLoader();
    const confirm = useConfirm();
    const today = moment().format(serverDateFormat);
    const getSeedDates = () => {
        if (!initialDate) return [today, today];
        const clamped = toServerDateFormat(moment.max(moment(initialDate), moment().startOf('day')));
        return [clamped, clamped];
    };
    const [ratesData, setRatesData] = useState([]);
    const [roomTypesData, setRoomTypesData] = useState([]);
    const [dates, setDates] = useState(getSeedDates);
    const [open, setOpen] = useState(startOpen);
    const isOpen = open || openProp;
    const [days, setDays] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [hotelLevel, setHotelLevel] = useState(false);
    const [roomTypes, setRoomTypes] = useState([]);
    const [rates, setRates] = useState([]);
    const [priceGroups, setPriceGroups] = useState([]);
    const [products, setProducts] = useState([]);
    const [min, setMin] = useState('');
    const [max, setMax] = useState('');
    const [restriction, setRestriction] = useState();
    const [packages, setPackages] = useState([]);
    const [params, setParams] = useState(null);
    const [hotelId, setHotelId] = useState(HotelID);
    const [selectedDates, setSelectedDates] = useState([]);
    const [AllowExternalUpdate, setAllowExternalUpdate] = useState();
    const [selectedAddonIds, setSelectedAddonIds] = useState([]);
    const [selectedHotel, setSelectedHotel] = useState(HotelID);
    const [customerGroupIds, setCustomerGroupIds] = useState([]);
    const [customerIds, setCustomerIds] = useState([]);
    const [overrideCustomerRestrictions, setOverrideCustomerRestrictions] = useState(false);

    const PackagesSpec = useMemo(() => {
        const spec = {
            AddonIds: selectedAddonIds
        };
        
        // If FilterPackageIds exists, use them as filter (not as value)
        if (params?.FilterPackageIds?.length) {
            spec.Ids = params.FilterPackageIds;
        }
        
        return spec;
    }, [selectedAddonIds, params?.FilterPackageIds]);

    const PriceGroupSpec = React.useMemo(() => {
        return {
            hotelId,
            FromDate: dates[0] ? dates[0] : null,
            ToDate: dates[1] ? dates[1] : null,
            RoomTypeIds: roomTypes,
        };
    }, [hotelId, roomTypes, dates[0], dates[1]]);

    const ProductsSpec = React.useMemo(() => {
        return {
            hotelId,
            FromDate: dates[0] ? dates[0] : null,
            ToDate: dates[1] ? dates[1] : null,
            IgnoreRestrictions: true,
            PackageIDs: packages,
            IgnoreDayUse: true,
            RoomTypeIds: roomTypes,
            RateIds: rates
        };
    }, [hotelId, roomTypes, rates, dates[0], dates[1], packages]);

    useEffect(() => {
        if (controller) {
            window[controller] = async (action, params) => {
                switch (action) {
                    case 'open':
                        if (params) {
                            setDates([
                                params.StartDate ? toServerDateFormat(params.StartDate) : today,
                                params.EndDate ? toServerDateFormat(params.EndDate) : today
                            ]);
                            setRates(params.RateID ? [params.RateID] : []);
                            setRoomTypes(
                                params.RoomTypeIds?.length
                                    ? params.RoomTypeIds
                                    : []
                            );
                            setProducts(params.ProductID ? [params.ProductID] : []);
                            setHotelId(params.HotelId || HotelID);
                            setParams(params);
                        } else {
                            setDates([today, today]);
                            setRates([]);
                            setPriceGroups([]);
                            setRoomTypes([]);
                            setProducts([]);
                            setParams(null);
                        }
                        setMin('');
                        setMax('');
                        setHotelLevel(false);
                        setOpen(true);
                        setRestriction(null);
                        setPackages([]);
                        setSelectedDates([]);
                        setOverrideCustomerRestrictions(false);
                        setCustomerIds([]);
                        setCustomerGroupIds([]);
                        break;
                    default:
                        break;
                }
            };
        }
    }, [HotelID, controller]);

    useEffect(() => {
        if (hotelLevel) {
            setRates([]);
            setPriceGroups([]);
            setRoomTypes([]);
            setProducts([]);
        }
    }, [hotelLevel]);

    // Fetch room types on spec change
    useEffect(() => {
        loader(true);
        const spec = {
            HotelId: hotelId,
            RateIds: rates
        };
        // If FilterRoomTypeIds exists, use them as filter (not as value)
        if (params?.FilterRoomTypeIds?.length) {
            spec.Ids = params.FilterRoomTypeIds;
        }
        RoomTypes.GetRoomTypes(spec)
            .then((data) => {
                setRoomTypesData(data);
                loader(false);
            })
            .catch((error) => {
                console.error('Error fetching room types:', error);
                loader(false);
            });
    }, [hotelId, rates]);

    // Fetch rates when spec is changed
    useEffect(() => {
        loader(true);
        Rates.GetRates({
            HotelId: hotelId,
            IncludeDayUse: false,
            IncludeRatePricingType: true,
            IsActive: true,
            InheritedRates: true,
            FromDate: dates[0] ? dates[0] : null,
            ToDate: dates[1] ? dates[1] : null,
            ExcludeRateCategoryIDs: [RateCategory.ExtraAddOn],
            RoomTypeIds: roomTypes,
        })
            .then((data) => {
                setRatesData(data);
                loader(false);
            })
            .catch((error) => {
                console.error('Error fetching rates:', error);
                loader(false);
            });
    }, [hotelId, roomTypes, dates[0], dates[1]]);

    // Fetch rates when price groups are selected
    useEffect(() => {
        if (priceGroups?.length > 0) {
            loader(true);
            Rates.GetRates({
                HotelId: hotelId,
                PriceGroupIds: priceGroups,
                RoomTypeIds: roomTypes,
                IncludeDayUse: false,
                ExcludeRateCategoryIDs: [RateCategory.ExtraAddOn]
            })
                .then((fetchedRates) => {
                    if (fetchedRates && fetchedRates.length > 0) {
                        const rateIds = fetchedRates.map(r => r.Id);
                        // Merge with existing rates, avoiding duplicates
                        setRates(prevRates => {
                            const combined = [...prevRates, ...rateIds];
                            return [...new Set(combined)];
                        });
                    }
                    loader(false);
                })
                .catch((error) => {
                    console.error('Error fetching rates from price groups:', error);
                    loader(false);
                });
        }
    }, [priceGroups, roomTypes, hotelId]);

    // Fetch packages when addons are selected
    useEffect(() => {
        (async () => {
            if (selectedAddonIds?.length > 0) {
                try {
                    loader(true);
                    const fetchedPackages = await Packages.GetPackages({
                        AddonIds: selectedAddonIds
                    });

                    if (fetchedPackages && fetchedPackages.length > 0) {
                        const packageIds = fetchedPackages.map(p => p.Id);
                        // Merge with existing packages, avoiding duplicates
                        setPackages(prevPackages => {
                            const combined = [...prevPackages, ...packageIds];
                            return [...new Set(combined)];
                        });
                    }
                    loader(false);
                } catch (error) {
                    console.error('Error fetching packages from addons:', error);
                    loader(false);
                }
            }
        })();
    }, [selectedAddonIds]);

    useEffect(() => {
        if (overrideCustomerRestrictions) {
            setCustomerGroupIds(null);
            setCustomerIds(null);
        }
    }, [overrideCustomerRestrictions]);

    useEffect(() => {
        if (selectedHotel) {
            setHotelId(selectedHotel)
        }
    }, [selectedHotel]);

    const checkIfValid = () => {
        const missingFields = []

        if (!selectedHotel) {
            missingFields.push(text("Hotel"))
        }

        if (missingFields.length === 0) {
            return true;
        }

        onWarning(text("MissingFields").replace('{0}', missingFields.join(", ")));
        return false;
    }

    const saveAction = async (close) => {

        const isValid = checkIfValid();
        if (!isValid) return

        // Check if multiple rates are being updated
        if (rates.length === 0) {
            const isConfirm = await confirm({
                content: message('PleaseNoteThatYouAreAboutToChangeMultipleRates'),
                confirmButton: {
                    content: text('Yes'),
                    positive: true,
                },
                cancelButton: {
                    content: text('No'),
                    negative: true,
                },
            });

            if (!isConfirm) return;
        }

        setIsSaving(true);
        loader(true);
        try {
            const alldates = selectedDates.length
                ? selectedDates
                : [
                    {
                        startDate: dates[0],
                        endDate: dates[1],
                    },];
            let productIds = products;
            if (products.length === 0 && packages.length > 0) {
                productIds = (await Products.GetProducts(ProductsSpec)).map(p => p.Id);
                if (productIds.length === 0) {
                    onWarning(message("NoProductsWithinThesePackages"));
                    setIsSaving(false);
                    loader(false);
                    return;
                }
            }

            // Make one call per date range with all rates
            const updated = await Promise.all(
                alldates.map(async (date) => {
                    return LiveUpdateHierarchyData({
                        StartDate: date.startDate,
                        EndDate: date.endDate,
                        WeekDays: days,
                        RoomTypeIds: roomTypes,
                        RateIds: rates,
                        Max: max,
                        Min: min,
                        ProductIds: productIds.filter(p => p),
                        HotelId: hotelId,
                        ForceHotelRestriction: hotelLevel,
                        RestrictionTypeId: restriction,
                        AllowExternalUpdate,
                        CustomerIds: customerIds,
                        CustomerGroupIds: customerGroupIds,
                        OverrideCustomerRestrictions: overrideCustomerRestrictions,
                    });
                })
            );

            if (onSave) {
                onSave(updated);
            }
            if (params && params.OnSave) {
                params.OnSave(updated);
            }
            onSuccess();
            if (close) {
                closeAction();
            }
        } catch (error) {
            console.error(error);
            error.status === 400 ? onWarning(error) : onError(error);
        }
        loader(false);
        setIsSaving(false);
    };

    const closeAction = () => {
        setOpen(false);
        resetFields();
        onClose?.();
    }

    useEffect(() => {
        if (!isOpen) {
            resetFields();
        }
    }, [isOpen]);

    // MonthlySideMenu opens this popup declaratively via the `open`/`initialDate`
    // props (no `controller`); dates was only ever seeded once at mount, so
    // picking a different day cell before opening the popup left it showing
    // whatever date was current the first time the component mounted.
    useEffect(() => {
        if (openProp) {
            setDates(getSeedDates());
        }
    }, [openProp]);

    const isSaveDisabled = () => {
        return (
            isSaving ||
            !(AllowExternalUpdate !== undefined || restriction || min || max) ||
            !(hotelLevel || roomTypes.length > 0 || rates.length > 0 || packages.length > 0 || products.length > 0)
        );
    };

    const addDateToList = () => {
        // Determine if only a single date is selected
        const singleDate = !dates[1] || dates[0] === dates[1];
        const startDate = dates[0];
        const endDate = singleDate ? dates[0] : dates[1];

        if (
            selectedDates.some(
                (d) =>
                    moment(d.startDate).isSame(startDate, 'day') &&
                    moment(d.endDate).isSame(endDate, 'day')
            )
        ) {
            onWarning(text('WarningDuplicateDates'));
            return;
        }
        setSelectedDates((selectedDates) =>
            selectedDates.concat({
                startDate,
                endDate,
            })
        );
    };

    const handleDatesChanged = (newValue) => {
        if (newValue) {
            setDates(newValue);
        } else {
            setDates(getSeedDates());
        }
    };

    const onRemoveDateFromSelectedDates = (index, e) => {
        if (e) {
            setSelectedDates((selectedDates) =>
                selectedDates.filter((_, i) => i !== index)
            );
        }
    };

    const resetFields = () => {
        setHotelLevel(false);
        setSelectedDates([]);
        setDates(getSeedDates());
        setRoomTypes([]);
        setSelectedAddonIds([]);
        setRestriction(null);
        setProducts([]);
        setRates([]);
        setPriceGroups([]);
        setAllowExternalUpdate(null);
        setMin('');
        setMax('');
        setSelectedHotel(HotelID)
        setOverrideCustomerRestrictions(false);
        setCustomerIds([]);
        setCustomerGroupIds([]);
    };

    const memoDates = useMemo(() => {
        return selectedDates.length
            ? selectedDates
            : [{ startDate: dates[0], endDate: dates[1] }];
    }, [dates, selectedDates]);

    return (
        <ValidateModalPermission
            open={isOpen}
            onClose={onClose}
            actionId={PermissionsActions.AccessRestrictionsPopup}
        >
            <Modal
                size={isMobileOnly ? 'fullscreen' : 'tiny'}
                className={isMobileOnly ? 'mobile' : ''}
                open={isOpen}
                style={{ zIndex: 1000000 }}
                {...rest}
            >
                {!isMobileOnly && (
                    <Modal.Header>{text('UpdateRestrictions')}</Modal.Header>
                )}
                <Modal.Content
                    style={
                        isMobileOnly
                            ? { overflowY: 'auto' }
                            : { maxHeight: 600, overflowY: 'auto' }
                    }
                >
                    <Form size='small'>
                        <Form.Group
                            unstackable
                            widths={'equal'}
                            style={{ flexWrap: 'wrap' }}
                        >
                            {selectedDates.map((selectedDate, index) => (
                                <Label.Group key={index}>
                                    <Label as='a'>
                                        {moment(selectedDate.startDate).format('DD/MM/YYYY')}-
                                        {moment(selectedDate.endDate).format('DD/MM/YYYY')}
                                        <Icon
                                            key={index}
                                            name={'delete'}
                                            onClick={onRemoveDateFromSelectedDates.bind(this, index)}
                                        />
                                    </Label>
                                </Label.Group>
                            ))}
                        </Form.Group>
                        <Form.Group unstackable style={{ alignItems: 'flex-end' }}>
                            <Form.Field width={12} style={{ display: 'grid' }}>
                                <DatePicker
                                    clearable={false}
                                    picktype='range'
                                    onChange={handleDatesChanged}
                                    label='Date Range'
                                    value={dates}
                                    minDate={moment().startOf('day').toDate()}
                                />
                            </Form.Field>
                            <Form.Field widths={4}>
                                <Button size='small' primary onClick={addDateToList}>
                                    {text('AddDate')}
                                </Button>
                            </Form.Field>
                        </Form.Group>
                        <Form.Field
                            label={text('Weekdays')}
                            control={WeekDaysSelect}
                            onChange={setDays}
                            dates={memoDates}
                        />
                        {!params?.DisableHotelLevel && (
                            <Form.Checkbox
                                label={text('HotelLevel')}
                                onChange={(e, { checked }) => setHotelLevel(checked)}
                                checked={hotelLevel}
                            />
                        )}
                        <Form.Field
                            label={text("Hotel")}
                            required
                            control={HotelsDropDown}
                            onChange={setSelectedHotel}
                            value={selectedHotel}
                            disabled={params?.LockHotel}
                        />
                        {!hotelLevel && (
                            <React.Fragment>
                                <Form.Field
                                    label={text('RoomType')}
                                    clearable
                                    multiple
                                    placeholder={text("All")}
                                    search={isBrowser}
                                    control={RoomTypesDropDown}
                                    data={roomTypesData}
                                    onChange={setRoomTypes}
                                    value={roomTypes}
                                />
                                <Form.Group widths={2} unstackable>
                                    <Form.Field
                                        label={text('PriceGroup')}
                                        clearable
                                        multiple
                                        placeholder={text("All")}
                                        search={isBrowser}
                                        control={PriceGroupsDropDown}
                                        spec={PriceGroupSpec}
                                        onChange={setPriceGroups}
                                        value={priceGroups}
                                    />
                                    <Form.Field
                                        label={text('Rates')}
                                        clearable
                                        multiple
                                        search={isBrowser}
                                        control={RatesDropDown}
                                        data={ratesData}
                                        onChange={setRates}
                                        value={rates}
                                        placeholder={text('All')}
                                        disabled={params?.LockRate}
                                    />
                                </Form.Group>
                                <Form.Group widths={2} unstackable>
                                    <Form.Field
                                        label={text("Addons")}
                                        control={DropDown}
                                        api={Addons.GetHotelChainAddons}
                                        multiple
                                        clearable
                                        onChange={(value) => setSelectedAddonIds(value)}
                                        valueField="Id"
                                        textField="Name"
                                        value={selectedAddonIds}
                                        placeholder={text('All')}
                                    />
                                    <Form.Field
                                        label={text("Package")}
                                        control={PackagesDropDown}
                                        textField="Name"
                                        valueField="Id"
                                        onChange={setPackages}
                                        spec={PackagesSpec}
                                        value={packages}
                                        clearable
                                        multiple
                                        placeholder={text('All')}
                                        includeRoomOnly
                                    />
                                </Form.Group>
                                {
                                    !window.SelfieSettings &&
                                    <Form.Field
                                        label={text("Product")}
                                        placeholder={text('All')}
                                        clearable
                                        multiple
                                        search={isBrowser}
                                        control={ProductsDropDown}
                                        descriptionField="RateName"
                                        spec={ProductsSpec}
                                        textField="Name"
                                        onChange={setProducts}
                                        value={products}
                                    />
                                }
                            </React.Fragment>
                        )}
                        <Form.Checkbox
                            label={text("OverrideCustomerRestrictions")}
                            onChange={(e, { checked }) => setOverrideCustomerRestrictions(checked)}
                            checked={overrideCustomerRestrictions}
                        />
                        {!overrideCustomerRestrictions && (
                            <Form.Group widths={2} unstackable>
                                <Form.Field
                                    label={text("Group")}
                                    placeholder={text("All")}
                                    control={CustomerGroupDropDown}
                                    value={customerGroupIds}
                                    multiple
                                    onChange={setCustomerGroupIds}
                                    clearable
                                />
                                <Form.Field
                                    label={text('Customer')}
                                    placeholder={text("All")}
                                    control={CustomerDropDown}
                                    spec={{ 
                                        IsCustomerCollecting: false,
                                        // If FilterCustomerIds exists, use them as filter (not as value)
                                        ...(params?.FilterCustomerIds?.length && { Ids: params.FilterCustomerIds })
                                    }}
                                    value={customerIds}
                                    multiple
                                    onChange={setCustomerIds}
                                    clearable
                                />
                            </Form.Group>
                        )}
                        <Form.Field
                            label={text('Restriction')}
                            control={RestrictionTypesDropDown}
                            onChange={setRestriction}
                            value={restriction}
                        />
                        <Form.Field
                            label={text("UpdateFromExternalChannel")}
                            control={AllowExternalUpdatesDropDown}
                            onChange={setAllowExternalUpdate}
                            value={AllowExternalUpdate}
                            clearable
                        />
                        <Form.Group widths={2} unstackable>
                            <Form.Field
                                label={text('MinNights')}
                                control={NumericInput}
                                onChange={setMin}
                                value={min}
                                min={1}
                                max={999}
                                digits={0}
                                nullable
                            />
                            <Form.Field
                                label={text('MaxNights')}
                                control={NumericInput}
                                onChange={setMax}
                                value={max}
                                min={1}
                                max={999}
                                digits={0}
                                nullable
                            />
                        </Form.Group>
                    </Form>
                </Modal.Content>
                <Modal.Actions>
                    {isBrowser && (
                        <Button
                            size={isMobileOnly ? 'tiny' : 'small'}
                            disabled={isSaving}
                            negative
                            onClick={closeAction}
                        >
                            {text('Close')}
                        </Button>
                    )}
                    {isBrowser && (
                        <Button size={'small'} onClick={resetFields}>
                            {text('Reset')}
                        </Button>
                    )}
                    {isBrowser && (
                        <Button
                            size={isMobileOnly ? 'tiny' : 'small'}
                            disabled={isSaveDisabled()}
                            loading={isSaving}
                            positive
                            onClick={() => saveAction(true)}
                        >
                            {text('Saveandclose')}
                        </Button>
                    )}
                    <Button
                        size={isMobileOnly ? 'tiny' : 'small'}
                        disabled={isSaveDisabled()}
                        loading={isSaving}
                        positive
                        onClick={() => saveAction(false)}
                    >
                        {text('Save')}
                    </Button>
                </Modal.Actions>
            </Modal>
        </ValidateModalPermission>
    );
};

RestrictionsPopup.propTypes = {
    onClose: PropTypes.func,
    onSave: PropTypes.func,
    HotelID: PropTypes.any,
    controller: PropTypes.string,
    startOpen: PropTypes.bool,
    initialDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    open: PropTypes.bool,
};

export default RestrictionsPopup;
